import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import '../../../../components/Common/styles/skeleton.css';
import SkeletonLoader from '../../../../components/Common/SkeletonLoader';
import ImageWithBasePath from '../../../../components/Common/imageWithBasePath';
import Badge from '../../../../components/Common/ui/Badge';

// Filter Controls Component
interface FilterControlsProps {
  filter: 'all' | 'warning' | 'critical';
  setFilter: (filter: 'all' | 'warning' | 'critical') => void;
}

export const FilterControls: React.FC<FilterControlsProps> = ({ filter, setFilter }) => {
  const { t } = useTranslation();
  
  return (
    <div className="card mb-4">
      <div className="card-body">
        <div className="row align-items-center">
          <div className="col-md-6">
          </div>
          <div className="col-md-6">
            <div className="d-flex justify-content-md-end">
              <div className="btn-group" role="group">
                <button
                  type="button"
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  {t('all')}
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'warning' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('warning')}
                >
                  {t('warning')}
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'critical' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('critical')}
                >
                  {t('critical')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// No Alerts Found Component
export const NoAlertsFound: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="card">
      <div className="card-body text-center py-5">
        <i className="ti ti-user-check text-success ti-3x mb-3"></i>
        <h5>{t('no_alerts_found')}</h5>
        <p className="text-muted">{t('no_students_at_risk_message')}</p>
      </div>
    </div>
  );
};

// No Filtered Alerts Found Component
interface NoFilteredAlertsFoundProps {
  setFilter: (filter: 'all') => void;
}

export const NoFilteredAlertsFound: React.FC<NoFilteredAlertsFoundProps> = ({ setFilter }) => {
  const { t } = useTranslation();
  
  return (
    <div className="card">
      <div className="card-body text-center py-5">
        <i className="ti ti-filter text-muted ti-3x mb-3"></i>
        <h5>{t('no_alerts_found_for_filter')}</h5>
        <p className="text-muted">{t('try_different_filter')}</p>
        <button
          className="btn btn-outline-primary mt-2"
          onClick={() => setFilter('all')}
        >
          {t('show_all_alerts')}
        </button>
      </div>
    </div>
  );
};

// Student Alert Card Component
interface StudentAlertCardProps {
  alert: any; // Replace with proper type
}

export const StudentAlertCard: React.FC<StudentAlertCardProps> = ({ alert }) => {
  const { t } = useTranslation();
  
  return (
    <div className="card h-100">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3 p-2">
          <div className="d-flex align-items-center">
            <div className="avatar">
              <ImageWithBasePath
                src={alert.student.user.photo || 'assets/img/profiles/avatar-01.jpg'}
                alt={alert.student.user.firstName}
                className="avatar-img rounded-circle shadow-sm me-3"
                width={55}
              />
            </div>
            <div className="ml-3">
              <h5 className="mb-0 font-weight-bold">{`${alert.student.user.firstName} ${alert.student.user.lastName}`}</h5>
              <small className="text-muted">{alert.student.class?.name || t('no_class')}</small>
            </div>
          </div>
          <div>
            <Badge
              variant={alert.riskLevel === 'critical' ? 'danger' : 'warning'}
              text={alert.riskLevel}
              className="p-2 shadow-sm"
            />
          </div>
        </div>
        
        <div className="d-flex justify-content-between mb-2 px-2">
          <span className="text-muted">{t('total_hours')} :</span>
          <span className="font-weight-bold">{alert.totalAbsenceHours}</span>
        </div>
        
        <div className="progress mb-2 mx-2" style={{ height: '8px', borderRadius: '4px' }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ 
              width: `${alert.totalAbsenceHours > 0 
                ? (alert.excusedHours / alert.totalAbsenceHours) * 100 
                : 0}%` 
            }}
            aria-valuenow={alert.totalAbsenceHours > 0 
              ? (alert.excusedHours / alert.totalAbsenceHours) * 100 
              : 0}
            aria-valuemin={0}
            aria-valuemax={100}
          ></div>
          <div
            className="progress-bar bg-warning"
            role="progressbar"
            style={{ 
              width: `${alert.totalAbsenceHours > 0 
                ? (alert.pendingHours / alert.totalAbsenceHours) * 100 
                : 0}%` 
            }}
            aria-valuenow={alert.totalAbsenceHours > 0 
              ? (alert.pendingHours / alert.totalAbsenceHours) * 100 
              : 0}
            aria-valuemin={0}
            aria-valuemax={100}
          ></div>
        </div>
        
        <div className="d-flex justify-content-between small mb-2 px-3 py-1 rounded">
        <div className="d-flex align-items-center">
              <span
                className="d-inline-block bg-success rounded-circle me-2"
                style={{ width: '10px', height: '10px' }}
              />
              {t('excused')}: {alert.excusedPercentage}%
            </div>
            <div className="d-flex align-items-center">
              <span
                className="d-inline-block bg-warning rounded-circle me-2"
                style={{ width: '10px', height: '10px' }}
              />
              {t('pending')}: {alert.pendingPercentage}%
            </div>
        </div>
        
        <div className="mt-2 pt-2 border-top">
          <div className="d-flex justify-content-between align-items-center mb-2 px-2">
            <span className="text-muted font-weight-medium">{t('threshold')} :</span>
            <div className="text-right">
              <div className="progress my-1" style={{ height: '5px', width: '100px', borderRadius: '3px' }}>
                <div
                  className={`progress-bar ${alert.riskLevel === 'critical' ? 'bg-danger' : 'bg-warning'}`}
                  role="progressbar"
                  style={{ width: `${alert.percentageOfThreshold * 100}%` }}
                  aria-valuenow={alert.percentageOfThreshold * 100}
                  aria-valuemin={0}
                  aria-valuemax={100}
                ></div>
              </div>
              <small className="font-weight-bold">{Math.round(alert.percentageOfThreshold * 100)}%</small>
            </div>
          </div>
          <div className="text-center mt-2">
            <Link 
              to={`/absences/student/${alert.student.id}`} 
              className="btn btn-sm btn-outline-primary btn-block py-1"
            >
              {t('view_details')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton Loading Component
export const SkeletonLoading: React.FC = () => {
  return (
    <>
      {/* Skeleton for filter controls */}
      <div className="card mb-4 skeleton-list-item">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <SkeletonLoader type="text" width="150px" height="24px" variant="large" />
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-md-end">
                <SkeletonLoader type="button" width="200px" height="38px" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Skeleton for alert cards */}
      <div className="row">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="col-md-6 col-lg-4 mb-4" style={{ '--item-index': i } as React.CSSProperties}>
            <div className="card skeleton-list-item">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <div className="mr-3">
                    <SkeletonLoader type="circle" width="50px" height="50px" index={i} />
                  </div>
                  <div className="flex-grow-1">
                    <SkeletonLoader type="text" width="120px" height="20px" className="mb-1" index={i} />
                    <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                  </div>
                  <div className="ml-auto">
                    <SkeletonLoader type="badge" width="70px" height="30px" index={i} />
                  </div>
                </div>
                
                <div className="d-flex justify-content-between mb-2">
                  <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                  <SkeletonLoader type="text" width="30px" height="16px" variant="small" index={i} />
                </div>
                
                <SkeletonLoader type="progress" width="100%" height="8px" className="mb-3" index={i} />
                
                <div className="d-flex justify-content-between small">
                  <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                  <SkeletonLoader type="text" width="80px" height="16px" variant="small" index={i} />
                </div>
                
                <div className="mt-3 pt-2 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <SkeletonLoader type="text" width="70px" height="16px" variant="small" index={i} />
                    <SkeletonLoader type="badge" width="40px" height="24px" index={i} />
                  </div>
                  <SkeletonLoader type="button" width="100%" height="38px" index={i} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
